// src/pages/screens/DatabaseListScreen.js
import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Select, message, Button, Input, Table, Modal } from "antd";
import axios from "axios";
import { AUTH_TOKEN, DOMAIN_ID, ORG_ID, REACT_APP_API } from "../../constants";
import { Database, Plus, Search, Trash2 } from "lucide-react";
import { AN_COLORS } from "../../design-system/tokens";
import {
  CountChip,
  FilterCard,
  FilterField,
  MonoCell,
  PageHeader,
  PageShell,
  RowActionMenu,
  StatusPill,
  TableCard,
  anTableProps,
  tableEmpty,
} from "../../design-system/tableUi";

const AgentListScreen = ({ showNotification, onDatabaseSelect }) => {
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [agentNames, setAgentName] = useState([]); // from getUniqueDatabasenames
  const [agentList, setAgentList] = useState([]); // from listDatabase
  const [totalAgentCount, setTotalAgentCount] = useState(0); // from listDatabase

  // Filters
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedHost, setSelectedHost] = useState(null);
  // Mirrors the host input so typing stays responsive while the fetch is debounced.
  const [hostInput, setHostInput] = useState("");
  const [hostSearchTimeout, setHostSearchTimeout] = useState(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize] = useState(10);

  // For user messages
  const [notification, contextHolder] = message.useMessage();

  // Actually fetch the DB list whenever filters/page change
  useEffect(() => {
    loadDatabaseData();
  }, [selectedAgent, selectedStatus, selectedHost, currentPage, pageSize]);

  const loadDatabaseData = async () => {
    setIsLoading(true);
    const filters = [];
    if (selectedAgent) {
      filters.push({
        filterParameter: "host_name",
        filterValue: selectedAgent,
      });
    }
    if (selectedStatus) {
      filters.push({ filterParameter: "status", filterValue: selectedStatus });
    }
    if (selectedHost) {
      filters.push({
        filterParameter: "agent_host_ip",
        filterValue: selectedHost,
      });
    }

    try {
      const { data, totalCount: tc } = await fetchDbList(
        filters,
        pageSize,
        currentPage + 1,
      );
      setAgentList(data);
      if (data?.length) {
        const names = data.map((item) => item.hostName);
        setAgentName(names);
      }
      setTotalCount(tc);
    } catch (error) {
      console.log("Failed");

      notification.error({ content: "Failed to fetch Agent list" });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDbList = async (filters, limit, pageId) => {
    try {
      const response = await axios.post(
        `${REACT_APP_API}/api/v1/databaseService/getDatabaseAgentHost`,
        {
          orgId: ORG_ID(),
          tenantId: DOMAIN_ID(),
          filters,
          limit,
          page_id: pageId,
        },
        {
          headers: {
            "X-Authorization": AUTH_TOKEN(),
            withCredentials: true,
          },
        },
      );

      return {
        data: response.data?.agentHostData || [],
        totalCount: response.data?.count || 0,
      };
    } catch (error) {
      console.error("Error fetching agent list:", error?.response?.status, error?.message);
      setAgentList([]);
      setTotalAgentCount(0);
    }
  };

  const deleteAgent = (db) => {
    console.log("hostIP", db);
    Modal.confirm({
      title: `Are you sure you want to delete "${db.hostName}"?`,
      content: "This action cannot be undone.",
      okText: "Yes, Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          const payLoad = {
            orgId: ORG_ID(),
            tenantId: DOMAIN_ID(),
            AgentHostIp: db.agentHostAddress,
          };

          await axios.post(
            `${REACT_APP_API}/api/v1/databaseService/deleteAgentHost`,
            payLoad,
            {
              headers: {
                "Content-Type": "application/json",
              },
            },
          );
          notification.success("Agent deleted successfully");
          loadDatabaseData();
        } catch (err) {
          console.error("Delete failed", err?.response?.status, err?.message);
        }
      },
    });
  };

  const handleMenuClick = async (db, action) => {
    switch (action) {
      case "delete":
        deleteAgent(db);
        break;
      case "viewDatabases":
        navigate(`/pam/databases`, {
          state: { host: db.agentHostAddress },
        });
        break;
      default:
        break;
    }
  };

  const columns = [
    {
      title: "Agent host",
      dataIndex: "hostName",
      key: "hostName",
      render: (text) => (
        <span style={{ fontWeight: 500, color: AN_COLORS.ink }}>{text || "—"}</span>
      ),
    },
    {
      title: "Host address",
      dataIndex: "agentHostAddress",
      key: "agentHostAddress",
      render: (text) => <MonoCell>{text}</MonoCell>,
    },
    {
      title: "Status",
      dataIndex: "agentStatus",
      key: "agentStatus",
      width: 150,
      render: (text) => <StatusPill status={text} />,
    },
    {
      title: "",
      key: "actions",
      width: 56,
      align: "right",
      render: (_, record) => (
        <RowActionMenu
          items={[
            {
              key: "viewDatabases",
              icon: <Database size={13} />,
              label: "View databases",
              onClick: () => handleMenuClick(record, "viewDatabases"),
            },
            { type: "divider" },
            {
              key: "delete",
              danger: true,
              icon: <Trash2 size={13} />,
              label: "Delete agent",
              onClick: () => handleMenuClick(record, "delete"),
            },
          ]}
        />
      ),
    },
  ];

  const resetPage = () => setCurrentPage(0);

  const appliedFilters = [
    selectedAgent && {
      key: "Agent",
      label: `Agent host · ${selectedAgent}`,
      clear: () => {
        setSelectedAgent(null);
        resetPage();
      },
    },
    selectedStatus && {
      key: "Status",
      label: `Status · ${selectedStatus}`,
      clear: () => {
        setSelectedStatus(null);
        resetPage();
      },
    },
    selectedHost && {
      key: "Host",
      label: `Address · ${selectedHost}`,
      clear: () => {
        setSelectedHost(null);
        setHostInput("");
        resetPage();
      },
    },
  ].filter(Boolean);

  const clearFilters = () => {
    setSelectedAgent(null);
    setSelectedStatus(null);
    setSelectedHost(null);
    setHostInput("");
    resetPage();
  };

  return (
    <PageShell>
      {contextHolder}

      <PageHeader
        eyebrow="Admin · Database · Agents"
        title="Agents"
        subtitle="Database agents installed on your hosts. Each agent discovers and brokers MFA for its databases."
        chips={<CountChip count={totalCount} label={totalCount === 1 ? "Agent" : "Agents"} />}
        actions={
          <Link to="/pam/databases/add/linux">
            <Button type="primary" icon={<Plus size={13} strokeWidth={2.2} />}>
              Install Database Agent
            </Button>
          </Link>
        }
      />

      <FilterCard
        columns="1.6fr 1fr 1fr"
        applied={appliedFilters}
        onClearAll={clearFilters}
        showing={agentList.length}
        total={totalCount}
      >
        <FilterField label="Host address">
          <Input
            allowClear
            placeholder="Search by address (e.g. 52.142)"
            prefix={<Search size={14} color={AN_COLORS.muted} />}
            value={hostInput}
            style={{ height: 36 }}
            onChange={(e) => {
              const value = e.target.value;
              setHostInput(value);
              if (hostSearchTimeout) clearTimeout(hostSearchTimeout);
              setHostSearchTimeout(
                setTimeout(() => {
                  setSelectedHost(value || null);
                  setCurrentPage(0);
                }, 500),
              );
            }}
          />
        </FilterField>

        <FilterField label="Agent host">
          <Select
            showSearch
            allowClear
            placeholder="Any agent host"
            style={{ width: "100%", height: 36 }}
            value={selectedAgent}
            onChange={(value) => {
              setSelectedAgent(value ?? null);
              setCurrentPage(0);
            }}
            filterOption={(input, option) =>
              (option?.value ?? "").toLowerCase().includes(input.toLowerCase())
            }
            options={[...new Set((agentNames || []).filter(Boolean).map((n) => n.split(" (")[0]))].map(
              (name) => ({ label: name, value: name }),
            )}
          />
        </FilterField>

        <FilterField label="Status">
          <Select
            allowClear
            placeholder="Any status"
            style={{ width: "100%", height: 36 }}
            value={selectedStatus}
            onChange={(value) => {
              setSelectedStatus(value ?? null);
              setCurrentPage(0);
            }}
            options={[
              { value: "Active", label: "Active" },
              { value: "Inactive", label: "Inactive" },
            ]}
          />
        </FilterField>
      </FilterCard>

      <TableCard>
        <Table
          {...anTableProps}
          columns={columns}
          dataSource={agentList}
          loading={isLoading}
          rowKey={(record) => record.id ?? record.agentHostAddress ?? record.hostName}
          locale={tableEmpty(
            isLoading,
            appliedFilters.length > 0
              ? "No agents match your filters."
              : "No agents registered yet — install a database agent to get started.",
          )}
          pagination={{
            current: currentPage + 1,
            pageSize,
            total: totalCount,
            showSizeChanger: false,
            onChange: (page) => setCurrentPage(page - 1),
          }}
        />
      </TableCard>
    </PageShell>
  );
};

export default AgentListScreen;
